import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Alert, Box, Button, Chip, MenuItem, Stack, TextField, Typography } from '@mui/material'
import { saveAIFeedback } from '../../service'
import { timestamp } from './uploadTimeline'

const creditLabel = credit => !credit || credit.status === 'unknown'
  ? 'Unconfirmed'
  : `${credit.jersey ? `#${credit.jersey}` : 'Jersey unreadable'}${credit.status === 'candidate' ? ' · possible' : ' · AI identified'}`

export default function AIEvent ({ compact = false, readOnly = false, event, feedback, uploadId, runId, onSeek, onCreateHighlight, onFeedbackSaved }) {
  const [rating, setRating] = useState(feedback?.rating || 'unreviewed')
  const [note, setNote] = useState(feedback?.note || '')
  const [busy, setBusy] = useState(false)
  const [saved, setSaved] = useState(Boolean(feedback))
  const [error, setError] = useState('')
  useEffect(() => {
    if (feedback) { setRating(feedback.rating); setNote(feedback.note || ''); setSaved(true) }
  }, [feedback])
  const seek = seconds => {
    setError(onSeek(seconds) ? '' : 'Wait for video playback to load, then try again.')
  }
  const save = async () => {
    setBusy(true)
    setError('')
    try {
      const result = await saveAIFeedback(uploadId, { run_id: runId, finding_id: event.id, rating, note })
      onFeedbackSaved?.(result.feedback)
      setSaved(true)
    } catch (_) {
      setError('Correction was not saved. Retry, or reload if the review changed.')
    } finally { setBusy(false) }
  }
  return <Stack component="article" aria-label={event.title} spacing={1} sx={{ mt: 2, pb: 2 }}>
    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
      <Button size="small" onClick={() => seek(event.start_seconds)}>Watch {timestamp(event.start_seconds)}</Button>
      <Chip size="small" label={event.outcome_status === 'supported' ? `AI ${event.event_type} evidence` : 'AI event candidate'} />
      {onCreateHighlight && <Button size="small" variant="outlined" onClick={() => onCreateHighlight(event)}>Save highlight</Button>}
    </Stack>
    <Typography variant="subtitle1">{event.title}</Typography>
    {!compact && <Typography variant="body2">{event.observation}</Typography>}
    {!compact && <Typography variant="body2" color="text.secondary">{event.attribution}</Typography>}
    {event.event_type === 'goal' && <Box>
      <Typography variant="body2"><strong>Scorer:</strong> {creditLabel(event.scorer)}</Typography>
      <Typography variant="body2"><strong>Assist:</strong> {event.assists?.length ? event.assists.map(creditLabel).join(', ') : 'Unconfirmed'}</Typography>
    </Box>}
    {!!event.evidence?.length && <Box component="details">
      <Typography component="summary" variant="body2" sx={{ cursor: 'pointer' }}>Why this was flagged</Typography>
      {event.evidence.map((cue, i) => <Stack key={i} direction="row" alignItems="baseline" spacing={1}>
        <Button size="small" onClick={() => seek(cue.seconds)} aria-label={`Watch evidence at ${timestamp(cue.seconds)}`}>{timestamp(cue.seconds)}</Button>
        <Typography variant="body2">{cue.detail}</Typography>
      </Stack>)}
      {[event.scorer, ...(event.assists || [])].filter(Boolean).map((credit, i) => <Typography key={i} variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        {credit.identity_evidence} {credit.action_evidence}
      </Typography>)}
      {event.audio_summary && <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{event.audio_summary}</Typography>}
    </Box>}
    {!readOnly && <Box component="details">
      <Typography component="summary" variant="body2" sx={{ cursor: 'pointer' }}>Correct this play</Typography>
      <Stack spacing={1} sx={{ mt: 1 }}>
        <TextField select size="small" label="Play assessment" value={rating} disabled={busy}
          onChange={e => { setRating(e.target.value); setSaved(false) }}>
          <MenuItem value="unreviewed">Not reviewed</MenuItem><MenuItem value="helpful">Helpful</MenuItem>
          <MenuItem value="wrong_player">Wrong player</MenuItem><MenuItem value="incorrect_event">Incorrect event</MenuItem>
          <MenuItem value="missed_context">Missing context</MenuItem>
        </TextField>
        <TextField label="Player or play correction" size="small" multiline minRows={2} value={note} disabled={busy}
          inputProps={{ maxLength: 2000 }} onChange={e => { setNote(e.target.value); setSaved(false) }} />
        <Box><Button size="small" disabled={busy || saved} onClick={save}>{busy ? 'Saving...' : saved ? 'Correction saved' : 'Save correction'}</Button></Box>
      </Stack>
    </Box>}
    {error && <Alert severity="error">{error}</Alert>}
  </Stack>
}
AIEvent.propTypes = { onFeedbackSaved: PropTypes.func, compact: PropTypes.bool, readOnly: PropTypes.bool, event: PropTypes.object.isRequired, feedback: PropTypes.object, uploadId: PropTypes.string.isRequired, runId: PropTypes.string.isRequired, onSeek: PropTypes.func.isRequired, onCreateHighlight: PropTypes.func }
